import { resolve } from "src/gqty";
import { serializePermissionChecks } from "./types";
import type {
  Action,
  ActionPermissionCheck,
  ActionPermissionResults,
  PermissionCheckInput,
} from "./types";

export type ResolvedActionPermissions = {
  permitted: boolean;
  permissions: ActionPermissionResults;
};

export async function resolveActionPermissions(
  action: Pick<Action<any>, "permissions">,
  records: any[],
  params?: Record<string, any>,
): Promise<ResolvedActionPermissions> {
  if (!action.permissions) return { permitted: true, permissions: [] };
  const requirement =
    typeof action.permissions === "function"
      ? action.permissions({ records, params })
      : action.permissions;
  const checks: PermissionCheckInput[] = [];
  type Requirement = {
    checkIndex?: number;
    and?: Requirement[];
    or?: Requirement[];
  };
  const collect = (check: ActionPermissionCheck): Requirement => {
    const node: Requirement = {};
    if (check.permission !== undefined) {
      node.checkIndex = checks.length;
      checks.push(check);
    }
    if (check.and !== undefined) node.and = check.and.map(collect);
    if (check.or !== undefined) node.or = check.or.map(collect);
    return node;
  };
  const tree = Array.isArray(requirement)
    ? { and: requirement.map(collect) }
    : collect(requirement);
  if (checks.length === 0) return { permitted: false, permissions: [] };

  const serialized = serializePermissionChecks(checks);
  const results = await resolve(
    ({ query: { isPermitted } }) =>
      serialized.map((check) => isPermitted({ permissions: [check] }) ?? false),
    { cachePolicy: "no-cache" },
  );
  const evaluate = (node: Requirement): boolean => {
    const clauses: boolean[] = [];
    if (node.checkIndex !== undefined) {
      clauses.push(results[node.checkIndex] === true);
    }
    if (node.and !== undefined) {
      clauses.push(node.and.length > 0 && node.and.every(evaluate));
    }
    if (node.or !== undefined) {
      clauses.push(node.or.length > 0 && node.or.some(evaluate));
    }
    return clauses.length > 0 && clauses.every(Boolean);
  };
  return {
    permitted: evaluate(tree),
    permissions: checks.map((check, index) => ({
      [check.permission]: results[index] === true,
    })),
  };
}
